import React from "react";
import {
    Alert,
    Box,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Paper,
    Radio,
    Select,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { ClassificationIcon } from "../../../utils/classificationIcons";

function toBool(value) {
    return value === true || value === 1 || value === "1" || value === "true";
}

function assignmentId(item) {
    return Number(item?.classification_id || item?.id);
}

function cleanAssignments(assignments) {
    return (assignments || []).filter((item) => Number.isFinite(assignmentId(item)));
}

export default function SectionKlassifizierungen({
                                                     form,
                                                     setForm,
                                                     classifications,
                                                     classificationsLoading,
                                                     classificationsError,
                                                 }) {
    const activeClassifications = React.useMemo(
        () => (classifications || []).filter((c) => c.is_active === undefined || !!c.is_active),
        [classifications]
    );

    const getAssignment = (classificationId) => (
        cleanAssignments(form.classification_assignments)
            .find((item) => assignmentId(item) === Number(classificationId)) || null
    );

    const setAssignment = (classification, patch) => {
        const id = Number(classification.id);
        setForm((f) => {
            const existing = cleanAssignments(f.classification_assignments);
            const current = existing.find((item) => assignmentId(item) === id) || {
                classification_id: id,
                value: "",
                is_superior: false,
                is_primary: false,
                sort_order: existing.length,
            };
            const nextItem = { ...current, ...patch, classification_id: id };

            const hasValue = classification.value_type === "boolean"
                ? toBool(nextItem.value)
                : String(nextItem.value ?? "").trim() !== "";

            let next = existing.filter((item) => assignmentId(item) !== id);
            if (hasValue) {
                next.push(nextItem);
            }

            if (patch.is_primary) {
                next = next.map((item) => ({
                    ...item,
                    is_primary: assignmentId(item) === id,
                }));
            }

            if (!hasValue || patch.is_primary === false) {
                const hasPrimary = next.some((item) => !!item.is_primary);
                if (!hasPrimary) {
                    next = next.map((item, index) => ({ ...item, is_primary: index === 0 }));
                }
            }

            if (hasValue && !next.some((item) => !!item.is_primary)) {
                next = next.map((item, index) => ({ ...item, is_primary: index === 0 }));
            }

            next.sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
            return { ...f, classification_assignments: next };
        });
    };

    const clearAssignment = (classificationId) => {
        const id = Number(classificationId);
        setForm((f) => {
            const next = cleanAssignments(f.classification_assignments)
                .filter((item) => assignmentId(item) !== id);
            const hasPrimary = next.some((item) => !!item.is_primary);
            return {
                ...f,
                classification_assignments: hasPrimary
                    ? next
                    : next.map((item, index) => ({ ...item, is_primary: index === 0 })),
            };
        });
    };

    if (classificationsError) {
        return <Alert severity="error">{classificationsError}</Alert>;
    }

    if (classificationsLoading) {
        return (
            <Box sx={{ py: 2, textAlign: "center" }}>
                <CircularProgress size={20} />
            </Box>
        );
    }

    if (!activeClassifications.length) {
        return <Alert severity="info">Keine Klassifikationen vorhanden. Lege zuerst Klassifikationen an.</Alert>;
    }

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
                gap: 1.25,
                alignItems: "stretch",
            }}
        >
            {activeClassifications.map((classification) => {
                const assignment = getAssignment(classification.id);
                const value = assignment?.value ?? "";
                const checked = classification.value_type === "boolean"
                    ? toBool(value)
                    : String(value).trim() !== "";
                const isPrimary = !!assignment?.is_primary;
                const max = Math.max(1, Number(classification.max_value || 5));

                return (
                    <Paper
                        key={classification.id}
                        variant="outlined"
                        sx={{
                            p: 1.25,
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "minmax(170px, 220px) 170px minmax(160px, 1fr)",
                            },
                            gridTemplateAreas: {
                                xs: "\"title\" \"value\" \"options\"",
                                sm: "\"title value options\"",
                            },
                            columnGap: 1.5,
                            rowGap: 0.5,
                            alignItems: "center",
                        }}
                    >
                        <Box sx={{ gridArea: "title", display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                            <Box
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "50%",
                                    bgcolor: "action.hover",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                }}
                            >
                                <ClassificationIcon iconName={classification.icon_name} sx={{ color: "primary.main" }} />
                            </Box>
                            <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                >
                                    {classification.label_de || classification.name}
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                gridArea: "value",
                                minWidth: 0,
                                justifySelf: { xs: "stretch", sm: "start" },
                                width: { xs: "100%", sm: 170 },
                            }}
                        >
                            {classification.value_type === "rating" && (
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Wert</InputLabel>
                                    <Select
                                        label="Wert"
                                        value={value === null ? "" : String(value)}
                                        onChange={(e) => {
                                            const nextValue = e.target.value;
                                            if (nextValue === "") clearAssignment(classification.id);
                                            else setAssignment(classification, { value: nextValue });
                                        }}
                                    >
                                        <MenuItem value="">Nicht zugewiesen</MenuItem>
                                        {Array.from({ length: max }, (_, index) => String(index + 1)).map((option) => (
                                            <MenuItem key={option} value={option}>{option}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            {classification.value_type === "boolean" && (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={checked}
                                            onChange={(e) => {
                                                if (!e.target.checked) clearAssignment(classification.id);
                                                else setAssignment(classification, { value: "1" });
                                            }}
                                        />
                                    }
                                    label={checked ? "Zugewiesen" : "Nicht zugewiesen"}
                                    sx={{
                                        m: 0,
                                        width: "100%",
                                justifyContent: { xs: "flex-start", sm: "flex-end" },
                                        "& .MuiFormControlLabel-label": { fontSize: "0.875rem" },
                                    }}
                                />
                            )}

                            {classification.value_type === "text" && (
                                <TextField
                                    size="small"
                                    label="Wert"
                                    value={value || ""}
                                    onChange={(e) => {
                                        const nextValue = e.target.value;
                                        if (!nextValue.trim()) clearAssignment(classification.id);
                                        else setAssignment(classification, { value: nextValue });
                                    }}
                                    fullWidth
                                />
                            )}
                        </Box>

                        <Box
                            sx={{
                                gridArea: "options",
                                display: "flex",
                                gap: 1.25,
                                alignItems: "center",
                                justifyContent: "flex-start",
                                flexWrap: "wrap",
                            }}
                        >
                            {!!classification.supports_superior && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            disabled={!checked}
                                            checked={checked && !!assignment?.is_superior}
                                            onChange={(e) => setAssignment(classification, { is_superior: e.target.checked })}
                                        />
                                    }
                                    label="Superior"
                                    sx={{
                                        m: 0,
                                        "& .MuiFormControlLabel-label": { fontSize: "0.875rem" },
                                    }}
                                />
                            )}

                            <FormControlLabel
                                control={
                                    <Radio
                                        size="small"
                                        disabled={!checked}
                                        checked={checked && isPrimary}
                                        onChange={() => setAssignment(classification, { is_primary: true })}
                                    />
                                }
                                label="Erste Position"
                                sx={{
                                    m: 0,
                                    "& .MuiFormControlLabel-label": { fontSize: "0.875rem" },
                                }}
                            />
                        </Box>
                    </Paper>
                );
            })}
        </Box>
    );
}
